import {
  OrbiAgentBillingNavigation,
  OrbiAgentCardAction,
  OrbiAgentCardVariant,
  OrbiAgentInsuranceState,
  OrbiAgentResponseRenderedEvent,
  OrbiAgentResponseType,
} from '@patient-ui/patient-web/store';
import {
  ORBI_APPOINTMENT_ACTION,
  ORBI_RETURN_TYPE,
  OrbiAppointmentAction,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import { OrbiAppointment, OrbiMessage } from '@patient-ui/shared/models';
import {
  isOrbiMobileCheckInAvailable,
  mapOrbiFindLabData,
  orbiMessageLocksChat,
  resolveOrbiInsuranceTask,
} from '@patient-ui/shared/utils';

export const ORBI_AGENT_UNKNOWN = 'Unknown';

export const ORBI_SOURCE_PAGE_OTHER = 'Other';

const ORBI_SOURCE_PAGES: readonly (readonly [string, string])[] = [
  ['/portal/results', 'Results'],
  ['/portal/invoices', 'Billing'],
  ['/portal/appointments', 'Appointments'],
  ['/portal/orders', 'Orders'],
  ['/portal/dashboard', 'Dashboard'],
  ['/portal/ondemand', 'Purchase Your Own Lab Test'],
  ['/portal/account', 'Profile Settings'],
];

const ORBI_RESPONSE_TYPES: readonly (readonly [
  string,
  OrbiAgentResponseType
])[] = [
  [ORBI_RETURN_TYPE.PatientResults, 'results_card'],
  [ORBI_RETURN_TYPE.AppointmentDetails, 'appointment_card'],
  [ORBI_RETURN_TYPE.AppointmentFindLab, 'find_a_lab_card'],
  [ORBI_RETURN_TYPE.AppointmentPatientPass, 'patient_pass_card'],
  [ORBI_RETURN_TYPE.VisitHistory, 'rate_visit_card'],
  [ORBI_RETURN_TYPE.FindABill, 'find_a_bill_card'],
  [ORBI_RETURN_TYPE.PayABill, 'billing_redirect'],
  [ORBI_RETURN_TYPE.PaymentPlan, 'billing_redirect'],
  [ORBI_RETURN_TYPE.UpdateInsurance, 'billing_redirect'],
  [ORBI_RETURN_TYPE.Invoices, 'billing_redirect'],
  [ORBI_RETURN_TYPE.InvoiceHistory, 'billing_redirect'],
  [ORBI_RETURN_TYPE.AppointmentSchedule, 'appointment_schedule'],
];

const ORBI_BILLING_NAVIGATIONS: readonly (readonly [
  string,
  OrbiAgentBillingNavigation
])[] = [
  [ORBI_RETURN_TYPE.PayABill, 'pay_a_bill'],
  [ORBI_RETURN_TYPE.PaymentPlan, 'payment_plan'],
  [ORBI_RETURN_TYPE.UpdateInsurance, 'update_insurance'],
  [ORBI_RETURN_TYPE.Invoices, 'view_invoices'],
  [ORBI_RETURN_TYPE.InvoiceHistory, 'invoice_history'],
];

const ORBI_APPOINTMENT_CARD_ACTIONS: Readonly<
  Record<OrbiAppointmentAction, OrbiAgentCardAction>
> = {
  [ORBI_APPOINTMENT_ACTION.details]: 'appointment_details',
  [ORBI_APPOINTMENT_ACTION.checkIn]: 'mobile_check_in',
  [ORBI_APPOINTMENT_ACTION.provideInsurance]: 'provide_insurance',
  [ORBI_APPOINTMENT_ACTION.reviewInsurance]: 'review_insurance',
  [ORBI_APPOINTMENT_ACTION.updateInsurance]: 'update_insurance',
};

const ORBI_INSURANCE_STATES: readonly OrbiAgentInsuranceState[] = [
  'provide',
  'review',
  'update',
];

export function resolveOrbiSourcePage(url: string | null | undefined): string {
  const path = (url ?? '').toLowerCase();
  const match = ORBI_SOURCE_PAGES.find(([fragment]) => path.includes(fragment));
  return match ? match[1] : ORBI_SOURCE_PAGE_OTHER;
}

export function resolveOrbiResponseType(
  message: OrbiMessage
): OrbiAgentResponseType {
  if (message.guardrailTriggered) {
    return 'guardrail';
  }
  if (message.type === OrbiMessageType.Error) {
    return 'error';
  }

  const returnType = message.structuredData?.returnType ?? message.returnType;
  const match = ORBI_RESPONSE_TYPES.find(([candidate]) =>
    isReturnType(returnType, candidate)
  );
  return match ? match[1] : 'text';
}

export function resolveOrbiBillingNavigation(
  message: OrbiMessage
): OrbiAgentBillingNavigation | undefined {
  const returnType = message.structuredData?.returnType ?? message.returnType;
  const match = ORBI_BILLING_NAVIGATIONS.find(([candidate]) =>
    isReturnType(returnType, candidate)
  );
  return match ? match[1] : undefined;
}

/** Only the service: the payload's location and date are patient content. */
export function resolveOrbiScheduleServiceType(
  message: OrbiMessage
): string | undefined {
  const returnType = message.structuredData?.returnType ?? message.returnType;
  if (!isReturnType(returnType, ORBI_RETURN_TYPE.AppointmentSchedule)) {
    return undefined;
  }
  return mapOrbiFindLabData(message.structuredData?.raw).initialService;
}

export function buildOrbiResponseRenderedEvent(
  message: OrbiMessage,
  sessionId: string
): OrbiAgentResponseRenderedEvent {
  const responseType = resolveOrbiResponseType(message);
  const guardrailType = message.guardrailType?.trim();
  const billingNavigation =
    responseType === 'guardrail'
      ? undefined
      : resolveOrbiBillingNavigation(message);
  const serviceType =
    responseType === 'appointment_schedule'
      ? resolveOrbiScheduleServiceType(message)
      : undefined;

  return {
    response_type: responseType,
    agent: message.agent?.trim() || ORBI_AGENT_UNKNOWN,
    session_id: sessionId,
    ...(billingNavigation && { billing_navigation: billingNavigation }),
    ...(serviceType && { service_type: serviceType }),
    ...(responseType === 'guardrail' && {
      ...(guardrailType && { guardrail_type: guardrailType }),
      guardrail_locking: orbiMessageLocksChat(
        message.guardrailTriggered,
        message.guardrailType
      ),
    }),
  };
}

export function resolveOrbiAppointmentCardVariant(
  appointment: OrbiAppointment,
  now: Date = new Date()
): OrbiAgentCardVariant {
  if (appointment.isCheckedIn) {
    return 'checked_in';
  }
  return isOrbiMobileCheckInAvailable(appointment, now)
    ? 'within_30_min'
    : 'upcoming';
}

export function resolveOrbiAppointmentCardAction(
  action: OrbiAppointmentAction
): OrbiAgentCardAction {
  return ORBI_APPOINTMENT_CARD_ACTIONS[action] ?? 'appointment_details';
}

export function resolveOrbiInsuranceState(
  appointment: OrbiAppointment
): OrbiAgentInsuranceState | undefined {
  const task = resolveOrbiInsuranceTask(appointment);
  return ORBI_INSURANCE_STATES.find((state) => state === task);
}
